import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { downloadApiFile } from "../lib/downloadApiFile";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth, hasPermission } from "../context/AuthContext";
import { ArrowLeft, BookOpen, Download, Loader2, Pencil, Plus, RefreshCw, Save, Trash2, Upload } from "lucide-react";

const GROUP_LABELS = {
  system: "System / cash / freight",
  expense: "Expense ledgers",
  stock: "Stock items (by variety)",
  party: "Party overrides (optional)",
};

const SETUP_GROUP_LABELS = {
  connection: "Company",
  dates: "Dates",
  voucher_types: "Voucher types",
  inventory: "Inventory defaults",
  tds_natures: "TDS Nature of Payment (Form 140)",
  compliance: "Sync approval & Tally staging",
};

function SetupTab({ canManage }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [agentTokenConfigured, setAgentTokenConfigured] = useState(false);
  const [agentTokenNote, setAgentTokenNote] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tally/settings");
      setItems(data?.items || []);
      setForm({ ...(data?.settings || {}) });
      setAgentTokenConfigured(Boolean(data?.agent_token_configured));
      setAgentTokenNote(data?.agent_token_note || "");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const grouped = useMemo(() => {
    const map = {};
    for (const item of items) {
      const g = item.group || "other";
      if (!map[g]) map[g] = [];
      map[g].push(item);
    }
    return map;
  }, [items]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    setSaving(true);
    try {
      const { data } = await api.put("/tally/settings", { settings: form });
      setItems(data?.items || []);
      setForm({ ...(data?.settings || {}) });
      toast.success("Tally setup saved");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const ensureMasters = async () => {
    if (!canManage) return;
    setEnsuring(true);
    try {
      const customTypes = [
        form.freight_voucher_type,
        form.material_voucher_type,
        form.tds_voucher_type,
      ].filter((v) => String(v || "").trim());
      const { data } = await api.post(
        "/tally/mappings/ensure-masters",
        { voucher_type_names: customTypes },
        { timeout: 90000 },
      );
      const created = Number(data?.created ?? -1);
      const skipped = Number(data?.skipped ?? 0);
      const confirmed = Boolean(data?.agent_confirmed);
      const msg = data?.message || "Ensure Masters finished";
      if (data?.warning === "zero_mutations") {
        toast.error(msg);
      } else if (confirmed && created === 0 && skipped > 0) {
        toast.success(msg);
      } else if (confirmed) {
        toast.success(msg);
      } else {
        toast.success(msg);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setEnsuring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading setup…
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={save} data-testid="tally-setup-form">
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground space-y-1">
        <p>
          <strong className="text-foreground">Production tip:</strong> set company name, voucher types, and TDS nature
          names here so you do not need to edit server <code className="font-mono">.env</code>.
        </p>
        <p>
          TDS Nature names must match Tally exactly (Gateway → TDS Nature of Payments / Tax Classification).
          Examples: <code className="font-mono">Purchase of Any Goods</code>,{" "}
          <code className="font-mono">Commission or Brokerage - Others</code>,{" "}
          <code className="font-mono">Payments to Contractors (Individual/HUF)</code>.
        </p>
        <p>
          Educational / demo date snap: turn <strong className="text-foreground">on</strong> only for Educational Mode
          companies (dates snap to the 1st of the month). Turn <strong className="text-foreground">off</strong> on licensed production.
        </p>
        <p>
          Agent token:{" "}
          <Pill tone={agentTokenConfigured ? "success" : "danger"}>
            {agentTokenConfigured ? "configured on server" : "missing on server"}
          </Pill>
          {agentTokenNote ? <span className="ml-1">{agentTokenNote}</span> : null}
        </p>
        <p>
          Daily voucher sync pushes <strong className="text-foreground">vouchers only</strong> (no ledger/stock Alter).
          Use <strong className="text-foreground">Ensure Masters</strong> to Create a ledger/stock item only when that
          exact name is missing in Tally. If the same name already exists, it is left unchanged.
        </p>
      </div>

      {canManage ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={ensuring}
            onClick={ensureMasters}
            data-testid="tally-ensure-masters"
          >
            {ensuring ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            Ensure Masters (Create only)
          </Button>
        </div>
      ) : null}

      {!canManage ? (
        <p className="text-sm text-muted-foreground">You can view setup but need manage permission to edit.</p>
      ) : null}

      {Object.keys(SETUP_GROUP_LABELS).map((groupKey) => {
        const rows = grouped[groupKey] || [];
        if (!rows.length) return null;
        return (
          <SectionCard key={groupKey} title={SETUP_GROUP_LABELS[groupKey] || groupKey}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rows.map((item) => (
                <div key={item.key} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor={`setup-${item.key}`}>{item.label}</Label>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {item.source}
                    </span>
                  </div>
                  {item.type === "boolean" ? (
                    <label className="flex items-center gap-2 h-9 text-sm">
                      <input
                        id={`setup-${item.key}`}
                        type="checkbox"
                        className="h-4 w-4 rounded border-input"
                        checked={Boolean(form[item.key])}
                        disabled={!canManage}
                        onChange={(e) => setField(item.key, e.target.checked)}
                      />
                      <span>{form[item.key] ? "On" : "Off"}</span>
                    </label>
                  ) : item.type === "secret" ? (
                    <Input
                      id={`setup-${item.key}`}
                      type="password"
                      value={form[item.key] ?? ""}
                      disabled={!canManage}
                      onChange={(e) => setField(item.key, e.target.value)}
                      placeholder={item.configured ? "•••••••• (leave blank to keep)" : ""}
                      autoComplete="new-password"
                    />
                  ) : (
                    <Input
                      id={`setup-${item.key}`}
                      value={form[item.key] ?? ""}
                      disabled={!canManage}
                      onChange={(e) => setField(item.key, e.target.value)}
                      placeholder={String(item.default ?? "")}
                    />
                  )}
                  {item.help ? <p className="text-[11px] text-muted-foreground leading-snug">{item.help}</p> : null}
                </div>
              ))}
            </div>
          </SectionCard>
        );
      })}

      {canManage ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save setup
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reload} disabled={saving || loading}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Reload
          </Button>
        </div>
      ) : null}
    </form>
  );
}

export default function TallyMappingPage() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "tally.mappings.manage");
  const [pageTab, setPageTab] = useState("mappings");
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [agentConnected, setAgentConnected] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all");
  const [savedFilter, setSavedFilter] = useState("");
  const [form, setForm] = useState({
    mapping_key: "",
    custom_key: "",
    tally_ledger_name: "",
    tally_parent_group: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const excelInputRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tally/mappings");
      setMappings(data?.mappings || []);
      setCoverage(data?.coverage || null);
      setCatalog(data?.catalog || data?.coverage?.catalog || []);
      setAgentConnected(Boolean(data?.agent_connected));
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pageTab === "mappings") reload();
  }, [reload, pageTab]);

  const mappedKeys = useMemo(() => new Set(mappings.map((m) => m.mapping_key)), [mappings]);

  const filteredCatalog = useMemo(() => {
    if (groupFilter === "all") return catalog;
    return catalog.filter((c) => (c.group || "system") === groupFilter);
  }, [catalog, groupFilter]);

  const resolvedKey = (form.custom_key || form.mapping_key || "").trim();

  const resetForm = () => {
    setEditingId(null);
    setForm({ mapping_key: "", custom_key: "", tally_ledger_name: "", tally_parent_group: "" });
  };

  const filteredSaved = useMemo(() => {
    const q = savedFilter.trim().toLowerCase();
    if (!q) return mappings;
    return mappings.filter((m) => {
      const hay = `${m.mapping_key} ${m.mapping_label || ""} ${m.tally_ledger_name || ""} ${m.tally_parent_group || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [mappings, savedFilter]);

  const saveMapping = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    if (!resolvedKey || !form.tally_ledger_name.trim()) {
      toast.error("Enter a mapping key and the exact Tally ledger name");
      return;
    }
    setSaving(true);
    try {
      const body = {
        mapping_key: resolvedKey,
        tally_ledger_name: form.tally_ledger_name.trim(),
        tally_parent_group: form.tally_parent_group.trim() || null,
        is_active: true,
      };
      if (editingId) {
        await api.patch(`/tally/mappings/${editingId}`, body);
        toast.success("Mapping updated");
      } else {
        await api.post("/tally/mappings", body);
        toast.success("Mapping saved");
      }
      resetForm();
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeMapping = async (id) => {
    if (!canManage) return;
    if (!window.confirm("Remove this Tally ledger mapping?")) return;
    try {
      await api.delete(`/tally/mappings/${id}`);
      toast.success("Mapping removed");
      if (editingId === id) resetForm();
      reload();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const toggleActive = async (row) => {
    if (!canManage) return;
    try {
      await api.patch(`/tally/mappings/${row.id}`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Mapping deactivated" : "Mapping activated");
      reload();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const editMapping = (row) => {
    const inCatalog = catalog.some((c) => c.key === row.mapping_key);
    setEditingId(row.id);
    setForm({
      mapping_key: inCatalog ? row.mapping_key : "",
      custom_key: inCatalog ? "" : row.mapping_key,
      tally_ledger_name: row.tally_ledger_name || "",
      tally_parent_group: row.tally_parent_group || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pickRow = (row) => {
    setEditingId(null);
    setForm({
      mapping_key: row.key,
      custom_key: "",
      tally_ledger_name: row.label?.replace(/^(Stock|Expense|Party) · /, "") || "",
      tally_parent_group: row.default_parent_group || "",
    });
  };

  const downloadExcel = async () => {
    setExcelBusy(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadApiFile("/tally/mappings/excel", `tally-mappings-${stamp}.xlsx`);
      toast.success("Excel downloaded — fill tally_name column and upload");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExcelBusy(false);
    }
  };

  const uploadExcel = async (file) => {
    if (!file || !canManage) return;
    setExcelBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tally/mappings/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const n = data?.saved_count || 0;
      const skipped = data?.skipped_blank || 0;
      toast.success(`Imported ${n} mapping${n === 1 ? "" : "s"}${skipped ? ` · ${skipped} blank row(s) skipped` : ""}`);
      if ((data?.errors || []).length) {
        toast.warning(`${data.errors.length} row error(s): ${data.errors[0]}`);
      }
      reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExcelBusy(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="tally-mapping-page">
      <PageHeader
        title="Tally mapping"
        description="Map ERP ledgers to Tally, and configure company / voucher types / TDS natures for sync — without editing server .env."
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" asChild>
              <Link to="/accounts">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Accounts
              </Link>
            </Button>
            <Button type="button" variant="secondary" size="sm" asChild>
              <Link to="/accounts/ledger">
                <BookOpen className="h-3.5 w-3.5 mr-1" />
                View ledger
              </Link>
            </Button>
            {pageTab === "mappings" ? (
              <>
                <Button type="button" variant="outline" size="sm" onClick={downloadExcel} disabled={excelBusy || loading}>
                  {excelBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                  Download Excel
                </Button>
                {canManage ? (
                  <>
                    <input
                      ref={excelInputRef}
                      type="file"
                      accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      className="hidden"
                      onChange={(e) => uploadExcel(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={excelBusy}
                      onClick={() => excelInputRef.current?.click()}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      Upload Excel
                    </Button>
                  </>
                ) : null}
                <Button type="button" variant="outline" size="sm" onClick={reload} disabled={loading}>
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                  Refresh
                </Button>
              </>
            ) : null}
          </div>
        )}
      />

      <div className="flex flex-wrap items-end gap-1 border-b border-border pb-px">
        {[
          { id: "mappings", label: "Mappings" },
          { id: "setup", label: "Setup" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setPageTab(t.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              pageTab === t.id
                ? "border-bansal-ink text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
        <Link
          to="/accounts/tally-approvals"
          className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground ml-auto"
        >
          Tally approvals →
        </Link>
      </div>

      {pageTab === "setup" ? (
        <SetupTab canManage={canManage} />
      ) : (
        <>
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground space-y-1">
            <p><strong className="text-foreground">Parties:</strong> By default the ERP party name is used as the Tally ledger (must match exactly). Map <code className="font-mono">party:&#123;id&#125;</code> only to override.</p>
            <p><strong className="text-foreground">Stock items:</strong> Map <code className="font-mono">inventory:&#123;variety&#125;</code> (e.g. <code className="font-mono">inventory:1509_sathi</code>) to the Tally <em>stock item</em> name — not a ledger. Also map <code className="font-mono">purchase</code> to the Purchase account ledger used under stock allocation.</p>
            <p><strong className="text-foreground">Expenses:</strong> Map <code className="font-mono">expense:&#123;code&#125;</code> (mandi_cess, commission, loading, cd, …) to each Tally expense ledger.</p>
            <p><strong className="text-foreground">Narration:</strong> Sync includes voucher code, purchase, party, variety, vehicle, qtl, rate, invoice/parchi, bilty/route.</p>
            <p><strong className="text-foreground">Excel:</strong> Download the template, fill the <code className="font-mono">tally_name</code> column with exact Tally names, then Upload. Blank names are skipped; existing keys are updated.</p>
            {coverage?.note ? <p className="text-foreground/80">{coverage.note}</p> : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={agentConnected ? "success" : "danger"}>
              {agentConnected ? "Tally agent connected" : "Tally agent offline"}
            </Pill>
            {coverage ? (
              <Pill tone={coverage.healthy ? "success" : "warning"}>
                Required keys: {coverage.unmapped_count ? `${coverage.unmapped_count} missing` : "ready"}
              </Pill>
            ) : null}
            <Pill tone="muted">{mappings.length} saved mappings</Pill>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title={editingId ? "Edit mapping" : "Add / update mapping"}>
              {!canManage ? (
                <p className="text-sm text-muted-foreground">You can view mappings but need manage permission to edit.</p>
              ) : (
                <form className="space-y-3" onSubmit={saveMapping}>
                  {editingId ? (
                    <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      Editing saved mapping #{editingId}. Change the Tally name/group, then save — or cancel.
                    </p>
                  ) : null}
                  <div className="space-y-1.5">
                    <Label htmlFor="mapping-key">Pick from catalog</Label>
                    <select
                      id="mapping-key"
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={form.mapping_key}
                      onChange={(e) => {
                        const key = e.target.value;
                        const cat = catalog.find((c) => c.key === key);
                        setForm((f) => ({
                          ...f,
                          mapping_key: key,
                          custom_key: "",
                          tally_parent_group: f.tally_parent_group || cat?.default_parent_group || "",
                          tally_ledger_name: f.tally_ledger_name || (cat?.label || "").replace(/^(Stock|Expense|Party) · /, ""),
                        }));
                      }}
                    >
                      <option value="">Select key…</option>
                      {filteredCatalog.map((c) => (
                        <option key={c.key} value={c.key}>
                          [{c.group || "system"}] {c.key} — {c.label}{mappedKeys.has(c.key) ? " ✓" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="custom-key">Or type custom key</Label>
                    <Input
                      id="custom-key"
                      value={form.custom_key}
                      onChange={(e) => setForm((f) => ({ ...f, custom_key: e.target.value, mapping_key: "" }))}
                      placeholder="expense:my_charge · inventory:1121 · party:90"
                      className="font-mono text-xs"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tally-name">Exact Tally name (stock item or ledger)</Label>
                    <Input
                      id="tally-name"
                      value={form.tally_ledger_name}
                      onChange={(e) => setForm((f) => ({ ...f, tally_ledger_name: e.target.value }))}
                      placeholder="Stock item or ledger — exact Tally spelling"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tally-group">Tally parent group (optional)</Label>
                    <Input
                      id="tally-group"
                      value={form.tally_parent_group}
                      onChange={(e) => setForm((f) => ({ ...f, tally_parent_group: e.target.value }))}
                      placeholder="Primary (stock) / Purchase Accounts / Direct Expenses"
                    />
                  </div>
                  {resolvedKey ? (
                    <p className="text-xs font-mono text-muted-foreground">Saving as: {resolvedKey}</p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="submit" size="sm" disabled={saving || !resolvedKey}>
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                      {editingId ? "Update mapping" : "Save mapping"}
                    </Button>
                    {editingId ? (
                      <Button type="button" size="sm" variant="outline" onClick={resetForm}>
                        Cancel edit
                      </Button>
                    ) : null}
                  </div>
                </form>
              )}
            </SectionCard>

            <SectionCard
              title="Catalog to map"
              action={(
                <select
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                >
                  <option value="all">All groups</option>
                  {Object.entries(GROUP_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              )}
            >
              <div className="space-y-2 max-h-[28rem] overflow-y-auto">
                {(coverage?.unmapped || []).length > 0 && groupFilter === "all" ? (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-amber-800 mb-1.5">Required missing</p>
                    {(coverage.unmapped || []).map((a) => (
                      <button
                        key={`req-${a.key}`}
                        type="button"
                        disabled={!canManage}
                        onClick={() => pickRow(a)}
                        className="w-full text-left rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm hover:bg-amber-50 mb-1.5 disabled:opacity-60"
                      >
                        <span className="font-mono text-xs text-muted-foreground mr-2">{a.key}</span>
                        <span className="font-medium">{a.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                {filteredCatalog.filter((c) => !mappedKeys.has(c.key)).slice(0, 120).map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    disabled={!canManage}
                    onClick={() => pickRow(a)}
                    className="w-full text-left rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/30 disabled:opacity-60"
                  >
                    <span className="font-mono text-xs text-muted-foreground mr-2">{a.key}</span>
                    <span className="font-medium">{a.label}</span>
                    <span className="ml-2 text-[10px] uppercase text-muted-foreground">{a.group || "system"}</span>
                  </button>
                ))}
                {!filteredCatalog.filter((c) => !mappedKeys.has(c.key)).length && (
                  <p className="text-sm text-muted-foreground py-6 text-center">Nothing unmapped in this group.</p>
                )}
              </div>
            </SectionCard>
          </div>

          <SectionCard
            title="Saved mappings"
            action={(
              <Input
                value={savedFilter}
                onChange={(e) => setSavedFilter(e.target.value)}
                placeholder="Search key / Tally name…"
                className="h-8 w-48 text-xs"
              />
            )}
          >
            <div className="divide-y divide-border">
              {filteredSaved.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      <span className="font-mono text-xs text-muted-foreground mr-2">{m.mapping_key}</span>
                      {m.mapping_label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      → <span className="text-foreground font-medium">{m.tally_ledger_name}</span>
                      {m.tally_parent_group ? ` · ${m.tally_parent_group}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!String(m.mapping_key || "").startsWith("inventory:") ? (
                      <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" asChild>
                        <Link to={`/accounts/ledger?ledger=${encodeURIComponent(m.mapping_key)}`}>
                          <BookOpen className="h-3.5 w-3.5 mr-1" />
                          Ledger
                        </Link>
                      </Button>
                    ) : null}
                    <button
                      type="button"
                      disabled={!canManage}
                      onClick={() => toggleActive(m)}
                      title={canManage ? "Toggle active / inactive" : undefined}
                      className="disabled:cursor-default"
                    >
                      <Pill tone={m.is_active ? "success" : "muted"}>{m.is_active ? "Active" : "Inactive"}</Pill>
                    </button>
                    {canManage ? (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => editMapping(m)}
                          title="Edit mapping"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-700"
                          onClick={() => removeMapping(m.id)}
                          title="Remove mapping"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
              {!filteredSaved.length && (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {mappings.length
                    ? "No mappings match this search."
                    : "No mappings yet. Map stock varieties and expense codes above, then Sync Now from Accounts."}
                </p>
              )}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
